import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask, task } from 'ember-concurrency';

import { FacebookResponseStatus } from './types';
import { ErrorSeverity } from 'later/services/errors';
import { arrayDiff } from 'later/utils/array-methods';
import { fetchFacebookLoginName } from 'later/utils/facebook-graph-helpers';
import {
  InsufficientPermissionsError,
  UnknownLoginError,
  FetchPageError,
  ExtensionBlockedError
} from 'shared/errors/instagram-auth';

import type { FacebookLoginParams, FacebookPage, AccountsResponse } from './types';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type SocialProfileModel from 'later/models/social-profile';
import type UserModel from 'later/models/user';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';
import type MavelyAuthService from 'later/services/mavely-auth';
import type SocialAuthEventsService from 'later/services/social/auth-events';
import type { SocialAuthEvent, SocialAuthEventFlow, SocialAuthEventFlowId } from 'later/services/social/auth-events';
import type { UnwrapAsyncRelationships } from 'shared/types';

const scopes = [
  'business_management',
  'catalog_management',
  'instagram_basic',
  'instagram_content_publish',
  'instagram_manage_comments',
  'instagram_manage_insights',
  'instagram_manage_messages',
  'instagram_shopping_tag_products',
  'pages_manage_metadata',
  'pages_manage_posts',
  'pages_messaging',
  'pages_manage_engagement',
  'pages_read_engagement',
  'pages_read_user_content',
  'pages_show_list',
  'read_insights'
] as const;

const fetchAccountsParams = {
  fields: 'picture,username,name,name_with_location_descriptor,access_token,tasks,followers_count',
  limit: 1000
} as const;

type FacebookAuthEvent = Pick<SocialAuthEvent, 'stage' | 'reason' | 'errorClass' | 'errorMessage' | 'eventName'>;

export default class FacebookAuthService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare mavelyAuth: MavelyAuthService;
  @service('social/auth-events') declare authEvents: SocialAuthEventsService;
  @service declare store: StoreService;

  @tracked authResponse?: fb.AuthResponse;
  @tracked facebookPages?: FacebookPage[];
  @tracked showFacebookSelector = false;
  @tracked socialIdentityId?: string;

  socialAuthFlowId?: SocialAuthEventFlowId;
  socialAuthFlow: SocialAuthEventFlow = 'connect';
  socialAuthSocialProfileId?: string;

  get facebookSdk(): fb.FacebookStatic | undefined {
    return typeof FB === 'undefined' ? undefined : FB;
  }

  get currentUserModel(): UserModel {
    return this.auth.currentUserModel;
  }

  get facebookLoginParams(): FacebookLoginParams {
    return { return_scopes: true, scope: scopes.join(',') };
  }

  @action
  closeFacebookModal(): void {
    this.showFacebookSelector = false;
    this.facebookPages = [];
    this.authResponse = undefined;
    this.socialAuthFlowId = undefined;
    this.socialAuthFlow = 'connect';
    this.socialAuthSocialProfileId = undefined;
  }

  authenticate = dropTask(
    async ({
      socialIdentityId,
      refreshSocialProfile,
      socialAuthFlowId
    }: {
      socialIdentityId?: string;
      refreshSocialProfile?: SocialProfileModel;
      socialAuthFlowId?: SocialAuthEventFlowId;
    }) => {
      this.socialAuthFlowId = socialAuthFlowId;
      this.socialAuthFlow = refreshSocialProfile ? 'reconnect' : 'connect';
      this.socialAuthSocialProfileId = refreshSocialProfile?.id;
      const { facebookSdk } = this;

      if (!facebookSdk) {
        this.#emitAuthEvent({ stage: 'failed', reason: 'sdk_blocked' });
        return this.#showFacebookSdkError();
      }

      this.socialIdentityId = socialIdentityId || undefined;
      this.authResponse = undefined;

      await this.#login(facebookSdk)
        .then(async (response) => await this._handleResponse.perform(response, refreshSocialProfile))
        .catch((error) => this.#handleError(error));
    }
  );

  #showFacebookSdkError(): void {
    this.errors.log('@service:facebook-auth | Facebook Javascript SDK is unavailable.');
    this.alerts.warning(
      this.intl.t('alerts.account.controllers.groups.facebook.cant_load_sdk', {
        title: this.intl.t('shared_phrases.something_went_wrong')
      })
    );
  }

  #login(facebookSdk: fb.FacebookStatic): Promise<fb.StatusResponse> {
    return new Promise((resolve, reject) => {
      facebookSdk.login((response: fb.StatusResponse) => {
        if (response.status === FacebookResponseStatus.Connected) {
          this.#emitAuthEvent({ stage: 'callback' });
          resolve(response);
        } else if (response.status === FacebookResponseStatus.Unknown) {
          this.#emitAuthEvent({
            stage: 'failed',
            reason: 'access_denied',
            eventName: 'facebook_login_unknown'
          });
          reject(new UnknownLoginError(''));
        } else {
          this.#emitAuthEvent({
            stage: 'failed',
            reason: 'access_denied',
            eventName: `facebook_login_${response.status}`
          });
          reject(new Error(`Login error: ${response.status}`));
        }
      }, this.facebookLoginParams);
    });
  }

  _handleResponse = task(async (response: fb.StatusResponse, refreshSocialProfile?: SocialProfileModel) => {
    this.#logResponse(response);
    const declinedScopes = this.#getDeclinedScopes(response.authResponse.grantedScopes);
    if (declinedScopes.length) {
      this.#emitAuthEvent({
        stage: 'failed',
        reason: 'scope_declined',
        errorMessage: declinedScopes.join(',')
      });
      throw new InsufficientPermissionsError(
        declinedScopes.map((declinedScope) => declinedScope.replace(/_/g, ' ')).join(', ')
      );
    } else {
      this.authResponse = response.authResponse;
      await this._saveUserModelToken.perform(response);

      const didFetchFacebookPages = await this._fetchFacebookPages.perform();
      if (!didFetchFacebookPages) {
        return;
      }

      if (refreshSocialProfile) {
        await this._refreshSocialProfile.perform(refreshSocialProfile);
      } else {
        this.showFacebookSelector = true;
      }
    }
  });

  #logResponse(response: fb.StatusResponse): void {
    this.errors.log(
      '@service:facebook-auth | authenticate response',
      { data: JSON.stringify(response) },
      ErrorSeverity.Debug
    );
  }

  #getDeclinedScopes(grantedScopes: string | undefined): string[] {
    return grantedScopes ? arrayDiff(scopes, grantedScopes.split(',')) : [];
  }

  _saveUserModelToken = task(async (response: fb.StatusResponse) => {
    try {
      const { userID, accessToken } = response.authResponse;
      this.currentUserModel.setProperties({
        facebookUid: userID,
        facebookToken: accessToken
      });
      this.currentUserModel.save();
    } catch (error) {
      this.errors.log('@service:facebok-auth | Unable to #saveUserModelToken', error);
      throw new Error(this.intl.t('alerts.generic_error_message'));
    }
  });

  _fetchFacebookPages = task(async (): Promise<boolean> => {
    this.facebookPages = [];

    try {
      this.facebookPages = await this._fetchPages.perform();
      return true;
    } catch (error) {
      this.#handleFetchPagesError(error);
      return false;
    }
  });

  _fetchPages = task(async (): Promise<FacebookPage[]> => {
    return new Promise((resolve, reject) => {
      const { facebookSdk } = this;

      if (!facebookSdk) {
        return reject(new ExtensionBlockedError());
      }

      facebookSdk.api('/me/accounts', fetchAccountsParams, (accountsResponse: AccountsResponse) => {
        if (!accountsResponse) {
          return reject(new FetchPageError());
        }
        if (accountsResponse.error) {
          if (accountsResponse.error.error_subcode == 1357045) {
            return reject(new ExtensionBlockedError());
          }
          return reject(new Error(JSON.stringify(accountsResponse.error)));
        }
        resolve(accountsResponse.data);
      });
    });
  });

  #handleFetchPagesError(error: FetchPageError | ExtensionBlockedError | Error): void {
    if (error instanceof ExtensionBlockedError) {
      this.#emitAuthEvent({ stage: 'failed', reason: 'sdk_blocked' });
      this.alerts.warning(this.intl.t('errors.instagram_auth.request_blocked.message'));
    } else if (error instanceof FetchPageError) {
      this.#emitAuthEvent({ stage: 'failed', errorClass: error.name });
      this.alerts.warning(this.intl.t('errors.instagram_auth.fetch_page.message'));
    } else {
      this.#emitAuthEvent({ stage: 'failed', errorClass: error.name, errorMessage: error.message });
      this.alerts.warning(
        this.intl.t('errors.instagram_auth.fetch_page_subcode.message', {
          subcode: error.message
        })
      );
    }
  }

  #handleError(error: InsufficientPermissionsError | UnknownLoginError | string): void {
    if (error instanceof InsufficientPermissionsError) {
      this.alerts.warning(
        this.intl.t('errors.instagram_auth.insufficient_permissions.message', { permissions: error.message }),
        {
          title: this.intl.t('errors.instagram_auth.insufficient_permissions.title')
        }
      );
    } else if (error instanceof UnknownLoginError) {
      this.authResponse = undefined;
    } else {
      this.alerts.warning(error, { title: this.intl.t('shared_phrases.something_went_wrong') });
      this.authResponse = undefined;
    }
  }

  createSocialProfile = task(async (selectedPage: FacebookPage) => {
    if (!this.authResponse || !this.authResponse.grantedScopes || !selectedPage.id) {
      return;
    }
    const allProfiles = this.store.peekAll('social-profile');
    const socialProfile =
      allProfiles.find((profile) => profile.uid === selectedPage.id) || this.store.createRecord('social-profile');
    const socialProfileAttributes = {
      account: this.auth.currentAccount,
      avatarUrl: selectedPage.picture.data.url,
      facebookProfileType: 'page',
      group: this.auth.currentGroup,
      isFbPage: true,
      permissionScope: this.authResponse.grantedScopes.split(','),
      profileColorClass: 'fbblue',
      profileType: 'facebook',
      token: selectedPage.access_token,
      uid: selectedPage.id,
      followedBy: selectedPage.followers_count
    } satisfies Partial<UnwrapAsyncRelationships<SocialProfileModel>>;

    socialProfile.setProperties(socialProfileAttributes);

    if (this.socialIdentityId) {
      const socialIdentity = this.store.peekRecord('social-identity', this.socialIdentityId);
      socialProfile.set('socialIdentity', socialIdentity);
    }

    try {
      const savedProfile = await socialProfile.save({
        adapterOptions: { socialAuthFlowId: this.socialAuthFlowId }
      });
      this.alerts.success(
        this.intl.t('alerts.account.controllers.groups.facebook.added', { name: savedProfile.nickname })
      );
      savedProfile.get('socialIdentity');
      await this.mavelyAuth.refreshAfterProfileAdd.perform();
    } catch (error) {
      this.errors.log('@service:facebook-auth | Unable to createSocialProfile', error);
      if (error.isAdapterError && error?.errors?.[0]?.detail) {
        this.alerts.alert(error.errors[0].detail, {
          title: this.intl.t('shared_phrases.something_went_wrong')
        });
      } else {
        this.alerts.alert(error, {
          title: this.intl.t('shared_phrases.something_went_wrong')
        });
      }
      socialProfile.rollbackAttributes();
    }
  });

  #emitAuthEvent(event: FacebookAuthEvent): void {
    if (!this.socialAuthFlowId) {
      return;
    }

    void this.authEvents.emit({
      provider: 'facebook',
      flow: this.socialAuthFlow,
      flowId: this.socialAuthFlowId,
      ...(this.socialAuthSocialProfileId ? { socialProfileId: this.socialAuthSocialProfileId } : {}),
      isPopup: true,
      ...event
    });
  }

  _refreshSocialProfile = task(async (refreshSocialProfile: SocialProfileModel) => {
    if (!this.facebookPages || !this.authResponse || !this.authResponse.grantedScopes) {
      return;
    }

    const facebookPage = this.facebookPages?.find((facebookPage) => facebookPage.id === refreshSocialProfile.uid);

    if (facebookPage) {
      const socialProfileAttributes = {
        account: this.auth.currentAccount,
        avatarUrl: facebookPage.picture.data.url,
        group: this.auth.currentGroup,
        permissionScope: this.authResponse.grantedScopes.split(','),
        token: facebookPage.access_token,
        uid: facebookPage.id,
        followedBy: facebookPage.followers_count
      } satisfies Partial<UnwrapAsyncRelationships<SocialProfileModel>>;
      refreshSocialProfile.setProperties(socialProfileAttributes);
      refreshSocialProfile.save();
    } else {
      await this.#reportUnmatchedProfile(refreshSocialProfile);
    }
  });

  /**
   * Facebook Page profiles record no authorizing uid, so "you're signed in as someone else" and
   * "you never granted Later this Page" cannot be told apart here -- the message is true either
   * way, and naming the live account is what makes it actionable.
   */
  async #reportUnmatchedProfile(refreshSocialProfile: SocialProfileModel): Promise<void> {
    // Never behind the Graph lookup's await: it can time out, and a failure we never recorded
    // is worse than one reported without a name.
    this.errors.log(
      '@service:facebook-auth | _refreshSocialProfile',
      {
        socialProfileId: refreshSocialProfile.id,
        hasAccessToken: Boolean(this.authResponse?.accessToken)
      },
      ErrorSeverity.Debug
    );

    const loginName = await fetchFacebookLoginName();

    this.alerts.warning(
      loginName
        ? this.intl.t('account.controllers.groups.facebook.refresh_error', {
            htmlSafe: true,
            loginName,
            social_profile: refreshSocialProfile.nickname
          })
        : this.intl.t('errors.instagram_auth.refresh_error_unknown_login.message', {
            htmlSafe: true,
            social_profile: refreshSocialProfile.nickname
          }),
      {
        title: this.intl.t('alerts.account.controllers.groups.facebook.refresh_error_title')
      }
    );
  }

  hasAllPermissions(socialProfile: SocialProfileModel): boolean {
    return scopes.every((scope) => socialProfile.permissionScope?.includes(scope));
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/facebook-auth': FacebookAuthService;
  }
}
